import type { Dispatch } from "react";
import type { FxDeal } from "../../lib/fx.ts";
import type { Aktion } from "../../state/reduzierer.ts";
import {
  kurssicherungLaden,
  SSE_ENTITAET as KURSSICHERUNG_ENTITAET,
  zuordnungAendern,
  zuordnungAnlegen,
  zuordnungLoeschen,
} from "../../api/kurssicherung.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";

/**
 * Die drei App-Wege für FX-Bindungen (index.html:9600-9619,13634-13645).
 * Binden addiert zum vorhandenen Betrag; nichtpositive Beträge lösen die Bindung.
 * Nach dem Schreiben wird der Deal nachgeladen und im bestehenden Store ersetzt.
 * Lösen verwendet den Rohindex der ungefilterten allocations, wie SicherungDetail.
 * Keine Memoisierung: dieselben aktuellen Werte und neuen Handler je App-Render.
 * Historische Entscheidungen und Prüfpfade:
 * docs/superpowers/specs/2026-10-12-app-fachliche-schnitte-design.md.
 *
 * Jede Meldung dieses Hooks ist ein Fehler und geht seit der Fehleranzeige (Paket 1) in den Fehlerstapel;
 * einen Toast gibt es hier nicht.
 */
export function useFxZuordnungen(
  fxDeals: FxDeal[],
  versende: Dispatch<Aktion>,
) {
  function zuordnungSetzen(dealId: string, bestellungId: string, usd: number): void {
    const deal = fxDeals.find((d) => String((d as { id?: unknown }).id ?? "") === dealId);
    if (!deal) return;
    const vorhanden = (Array.isArray(deal.allocations) ? deal.allocations : []).find(
      (a) => String((a as { orderId?: unknown }).orderId ?? "") === bestellungId,
    ) as { id?: unknown } | undefined;
    const allocationId = String(vorhanden?.id ?? "");

    // Nichts zu schreiben: es gibt keine Zuordnung, und es soll auch keine geben. Dann gibt es auch nichts nachzuladen und
    // nichts zu melden. Frueher lief hier ein `Promise.resolve()` statt eines Schreibens in dieselbe Kette: scheiterte
    // dann das Nachladen des Deals, stand "Die Änderung der Zuordnung ist gespeichert ...", obwohl nichts geschrieben war.
    // Ueber `zahlungenSicherungBinden` (verlangt usd > 0) und `zahlungenSicherungLoesen` (geht von einer vorhandenen Zuordnung
    // aus) ist dieser Fall nicht erreichbar, nur ueber den direkten Aufruf von `zuordnungSetzen`.
    if (!vorhanden && !(usd > 0)) return;

    const geschrieben = !vorhanden
      ? zuordnungAnlegen(dealId, bestellungId, usd)
      : usd > 0
        ? zuordnungAendern(dealId, allocationId, usd)
        : zuordnungLoeschen(dealId, allocationId);

    geschrieben.then(
      () =>
        kurssicherungLaden(dealId)
          .then((neu) => {
            versende({
              art: "nachgeladen",
              entitaet: KURSSICHERUNG_ENTITAET,
              id: dealId,
              datensatz: neu,
            });
          })
          .catch((fehler: unknown) => {
            // R4: geschrieben ist geschrieben. Scheitert nur das Nachladen des Deals, ist das KEIN Fehlschlag
            // der Zuordnung ("konnte nicht gespeichert werden" waere falsch), sondern eine veraltete Anzeige.
            console.error(`Deal "${dealId}" konnte nach der Zuordnung nicht geladen werden:`, fehler);
            // R-Z2: kein Wiederholungsrat der Ursache hinter dem Erfolgssatz.
            meldeNachladeFehler(
              fehler,
              "Die Änderung der Zuordnung ist gespeichert, die Anzeige konnte aber nicht aktualisiert werden. Bitte neu laden.",
            );
          }),
      (fehler: unknown) => {
        // Der Fehler selbst ins Protokoll UND als Ursache in die Meldung: das Backend unterscheidet 404
        // (Deal/Zuordnung weg), 409 (Deckung reicht nicht) und 400 (Betrag unbrauchbar) - der feste Satz
        // allein tut das nicht; `fehlertext` holt den Servertext.
        console.error(`Zuordnung an "${dealId}" konnte nicht geschrieben werden:`, fehler);
        meldeFehler(fehler, "Die Zuordnung konnte nicht gespeichert werden.");
      },
    );
  }

  function zahlungenSicherungBinden(index: number, bestellungId: string, usd: number): void {
    const deal = fxDeals[index];
    if (!deal || !bestellungId || !(usd > 0)) return;
    const bisher = (Array.isArray(deal.allocations) ? deal.allocations : []).find(
      (a) => String((a as { orderId?: unknown }).orderId ?? "") === bestellungId,
    ) as { usd?: unknown } | undefined;
    zuordnungSetzen(
      String((deal as { id?: unknown }).id ?? ""),
      bestellungId,
      (Number(bisher?.usd) || 0) + usd,
    );
  }

  function zahlungenSicherungLoesen(index: number, zuordnungIndex: number): void {
    const deal = fxDeals[index];
    if (!deal) return;
    const zuordnung = (Array.isArray(deal.allocations) ? deal.allocations : [])[zuordnungIndex] as
      | { orderId?: unknown }
      | undefined;
    if (!zuordnung) return;
    zuordnungSetzen(
      String((deal as { id?: unknown }).id ?? ""),
      String(zuordnung.orderId ?? ""),
      0,
    );
  }

  return { zuordnungSetzen, zahlungenSicherungBinden, zahlungenSicherungLoesen };
}
